import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useGoalDuplexer } from './use-goal-duplexer';
import { queryClient, queryKeys } from './query-client';

// Client data layer for the home "opportunities" pile — the OPPORTUNITY goals an agent proposed across
// every workspace, waiting for the user to hand one over. Mirrors the native home widget fed by
// `loadAllOpportunities`. orion-web's CLIENT bundle must not import common-lib, so the shape lives here
// as a client-local contract matching what /api/opportunities returns.

/** One agent-proposed outcome. `workspaceId` is joined to a name from the workspaces list at render. */
export interface WebOpportunity {
  id: string;
  title: string;
  brief: string;
  workspaceId: string;
}

// Ids abandoned this session. The abandon write and the goal-query read path are eventually
// consistent — the goal-changed event our own abandon triggers can fire a duplexer refetch
// before the server's read side has caught up, which would otherwise resurrect the card we just
// optimistically removed. Every fetch filters these out until the id no longer comes back at all.
const dismissedIds = new Set<string>();

async function fetchOpportunities(): Promise<WebOpportunity[]> {
  const res = await apiFetch(`${API_BASE}/api/opportunities`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { opportunities?: WebOpportunity[] };
  const opportunities = data.opportunities ?? [];
  const returnedIds = new Set(opportunities.map((o) => o.id));
  // Once the server genuinely stops returning an id (its status has caught up), stop filtering it —
  // otherwise the set would grow for the rest of the session.
  for (const id of dismissedIds) {
    if (!returnedIds.has(id)) dismissedIds.delete(id);
  }
  return opportunities.filter((o) => !dismissedIds.has(o.id));
}

/** The pile's data source. `undefined` while loading (and on a failure before the first success) — the
 *  pile then renders nothing rather than an empty frame. */
export function useOpportunities(): WebOpportunity[] | undefined {
  const query = useQuery({ queryKey: queryKeys.opportunities, queryFn: fetchOpportunities });
  // An agent proposing (or the user pursuing) an opportunity changes the world — refresh with it.
  useGoalDuplexer(() => void query.refetch());
  return query.data;
}

/** The proposals belonging to ONE workspace, newest-first order preserved (the route already sorted them).
 *  `undefined` in, `undefined` out — an unresolved fetch or an unknown workspace is "not known yet", which
 *  the caller renders as nothing at all rather than as "no suggestions". */
export function workspaceOpportunities(
  all: WebOpportunity[] | undefined,
  workspaceId: string | undefined,
): WebOpportunity[] | undefined {
  if (!all || !workspaceId) return undefined;
  return all.filter((opportunity) => opportunity.workspaceId === workspaceId);
}

/** One workspace's slice of the same read — the Goals tab's "Opportunities" section.
 *
 *  `/api/opportunities` is deliberately UNSCOPED (the home pile aggregates every workspace) and every item
 *  carries its owning `workspaceId`, so the tab narrows that one cached response instead of adding a
 *  second, near-identical endpoint: the two surfaces then cannot drift onto different filters, and the tab
 *  costs no extra request. Native reaches the same rows the other way round — a per-workspace OPPORTUNITY
 *  query (`loadOpportunities`) that its home then aggregates (`loadAllOpportunities`).
 *
 *  Sharing the one query also means this section inherits `abandonOpportunity`'s optimistic dismissals for
 *  free (they are filtered inside `fetchOpportunities`), so a proposal swiped away on the home pile
 *  disappears from the Goals tab too rather than lingering on a second surface. */
export function useWorkspaceOpportunities(workspaceId: string | undefined): WebOpportunity[] | undefined {
  const all = useOpportunities();
  return useMemo(() => workspaceOpportunities(all, workspaceId), [all, workspaceId]);
}

/** Swiped away in the pile (mirrors native's swipe-left dismiss). Drops it from the cache immediately —
 *  optimistic, house style (see lib/conversations.ts) — then persists ABANDONED on the server, rolling
 *  the cache back if that fails so a lost network call never silently loses a proposal. */
export async function abandonOpportunity(id: string): Promise<void> {
  dismissedIds.add(id);
  const prev = queryClient.getQueryData<WebOpportunity[]>(queryKeys.opportunities);
  if (prev) {
    queryClient.setQueryData<WebOpportunity[]>(
      queryKeys.opportunities,
      prev.filter((o) => o.id !== id),
    );
  }
  let ok = false;
  try {
    const res = await apiFetch(`${API_BASE}/api/opportunities/abandon`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ goalId: id }),
    });
    ok = res.ok;
  } catch {
    ok = false;
  }
  if (!ok) {
    dismissedIds.delete(id);
    if (prev) queryClient.setQueryData<WebOpportunity[]>(queryKeys.opportunities, prev);
  }
}
